import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, interval, take } from 'rxjs';
import {
    Invitation,
    Registerer,
    RegistererOptions,
    RegistererState,
    Session,
    SessionState,
    URI,
    UserAgent,
    UserAgentOptions,
} from 'sip.js';

import { IUser } from '@dxp/core/models';
import { VoiceApiService } from '@dxp/core/api';
import { USER_STORE } from '@dxp/core/stores';
import { AudioDeviceService } from '../audio/audio-device.service';

type ServerHost = string;

interface ISipBinding {
    ua: UserAgent;
    registerer: Registerer;
}

@Injectable({ providedIn: 'root' })
export class SipService {
    localStream = signal<MediaStream | null>(null);
    remoteStream = signal<MediaStream | null>(null);
    public callState = signal<
        | 'idle'
        | 'registering'
        | 'registered'
        | 'ringing'
        | 'in-call'
        | 'ended'
        | 'error'
    >('idle');
    public incomingInvitation = signal<Invitation | null>(null);
    public registrationStatus = signal<
        | 'Not Registered'
        | 'Registering'
        | 'Registered'
        | 'Unregistered'
        | 'Error'
    >('Not Registered');

    readonly user = computed<IUser>(() => this.userStore.user());

    private bindings = new Map<ServerHost, ISipBinding>();
    private instanceId = this.ensureInstanceId();

    private currentSession: Session | null = null;
    private autoAnswer = signal(false);
    private peerConnection?: RTCPeerConnection;
    private shuttingDown = false;

    private ringingAudio: HTMLAudioElement = new Audio(
        '././assets/sounds/telephone-ring.mp3',
    );
    private dtmfAudio: HTMLAudioElement = new Audio();

    constructor() {
        this.ringingAudio.loop = true;
        window.addEventListener('beforeunload', () => {
            this.destroyAll();
        });
        window.addEventListener('online', () => {
            if (this.bindings.size === 0) this.refreshInitializeSipClients();
            else this.refreshInitializeSipClientsSafe(true);
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible')
                this.refreshInitializeSipClientsSafe(true);
        });

        window.addEventListener('offline', () => {
            console.warn('[SIP] browser offline');
            for (const { registerer } of this.bindings.values()) {
                this.runRegOp(registerer, () => registerer.register()).catch(
                    () => {},
                );
            }
        });

        // Every 1 minute: check all known servers are connected and registered
        interval(60_000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.checkConnectivity());

        // Every 1 hour: fetch fresh server list and reconcile registrations
        interval(60 * 60_000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.reconcileServers());
    }

    private audioDeviceService = inject(AudioDeviceService);
    private voiceApiService = inject(VoiceApiService);
    private userStore = inject(USER_STORE);
    private destroyRef = inject(DestroyRef);

    private lastServers: { uri: string }[] = [];
    private lastStunServers: string[] = [];
    private lastUsername = '';
    private lastPassword = '';

    private lastRegisterAt = 0;
    private readonly safetyRefreshMs = 2 * 60 * 1000;

    private regOpQueue = new WeakMap<Registerer, Promise<unknown>>();

    private runRegOp<T>(reg: Registerer, op: () => Promise<T>): Promise<T> {
        const prev = this.regOpQueue.get(reg) ?? Promise.resolve();
        const next = prev.catch(() => {}).then(op);
        this.regOpQueue.set(
            reg,
            next.then(
                () => undefined,
                () => undefined,
            ),
        );
        return next;
    }

    setAutoAnswer() {
        this.autoAnswer.set(true);
    }

    async initializeSipClients(
        servers: { uri: string }[],
        stunServers: string[],
        username: string,
        password: string,
    ) {
        this.lastServers = servers;
        this.lastStunServers = stunServers;
        this.lastUsername = username;
        this.lastPassword = password;

        await this.destroyAll();
        this.registrationStatus.set('Registering');
        this.callState.set('registering');
        const results = await Promise.allSettled(
            servers.map(s =>
                this.registerWithServer(s.uri, stunServers, username, password),
            ),
        );
        const allOk = results.every(r => r.status === 'fulfilled');
        this.registrationStatus.set(allOk ? 'Registered' : 'Error');
        this.callState.set(allOk ? 'registered' : 'error');

        this.voiceApiService.sipState('connected', {
            agentId: this.user().id,
            tenantId: this.user().tenant?.id ?? '',
        });
    }

    async logout() {
        await this.destroyAll();
        this.registrationStatus.set('Unregistered');
        this.callState.set('idle');
    }

    public async answerCall(): Promise<void> {
        const invitation = this.incomingInvitation();
        if (!invitation) {
            return;
        }
        invitation.stateChange.addListener(async state => {
            if (state === SessionState.Established) {
                this.callState.set('in-call');
                const remoteStream = new MediaStream();
                const sessionDescriptionHandler =
                    invitation.sessionDescriptionHandler as any;
                const peerConnection =
                    sessionDescriptionHandler?.peerConnection;
                if (peerConnection) {
                    peerConnection
                        .getReceivers()
                        .forEach((receiver: { track: MediaStreamTrack }) => {
                            if (receiver.track) {
                                remoteStream.addTrack(receiver.track);
                            }
                        });
                    this.peerConnection = peerConnection;
                    const audioElement = document.querySelector(
                        '#remoteAudio',
                    ) as HTMLAudioElement;
                    audioElement.srcObject = remoteStream;
                    await this.audioDeviceService.applySpeakerTo(audioElement);
                    audioElement.play();
                }
            } else if (state === SessionState.Terminated) {
                this.callState.set('ended');
                this.stopLocalStream();
                this.remoteStream.set(null);
            }
        });
        try {
            await invitation.accept({
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: this.audioDeviceService.micId()
                            ? {
                                  deviceId: {
                                      exact:
                                          this.audioDeviceService.micId() ?? '',
                                  },
                              }
                            : true,
                        video: false,
                    },
                },
            });
            this.currentSession = invitation;
            this.incomingInvitation.set(null);
            this.stopRingingSound();
        } catch (err: any) {
            const msg = String(err?.message ?? err ?? '');
            console.error('Failed to accept call:', msg);

            this.stopLocalStream();
            this.stopRingingSound();

            this.voiceApiService.sipState('failed', {
                agentId: this.user().id,
                tenantId: this.user().tenant?.id ?? '',
            });

            this.voiceApiService.log({
                agentId: this.user().id,
                tenantId: this.user().tenant?.id ?? '',
                errorMessage: msg,
            });

            await this.refreshInitializeSipClients();
        }
    }

    public hangUp(): void {
        if (
            this.currentSession &&
            this.currentSession.state !== SessionState.Terminated
        ) {
            this.currentSession.bye();
        }
        this.currentSession = null;
        this.stopLocalStream();
        this.stopRingingSound();
        this.incomingInvitation.set(null);
        this.peerConnection = undefined;
        this.callState.set('ended');
    }

    mute(): void {
        this.peerConnection?.getSenders().forEach(s => {
            if (s.track) s.track.enabled = false;
        });
    }

    unmute(): void {
        this.peerConnection?.getSenders().forEach(s => {
            if (s.track) s.track.enabled = true;
        });
    }

    sendDtmf(dtmf: string) {
        const audioKey = dtmf === '#' ? 'hash' : dtmf === '*' ? 'star' : dtmf;
        this.dtmfAudio.src = `././assets/sounds/dtmf/Dtmf-${audioKey}.wav`;
        this.dtmfAudio.load();
        this.dtmfAudio.play();
        (this.currentSession as any)?.sessionDescriptionHandler?.sendDtmf?.(
            dtmf,
        );
    }

    /**
     * Toggles the mute - Allows a param to be passed in to mute the call
     *
     * @param unMute - True unmutes / False mutes
     */
    toggleMute(unMute: boolean): void {
        this.peerConnection?.getSenders().forEach(s => {
            if (s.track) s.track.enabled = unMute;
        });
    }

    private ensureInstanceId(): string {
        let id = localStorage.getItem('sip.instanceId');
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem('sip.instanceId', id);
        }
        return id;
    }

    private async registerWithServer(
        serverHost: string,
        stunServers: string[],
        username: string,
        password: string,
    ) {
        const uri = new URI('sip', username, serverHost);
        const wsServer = `wss://${serverHost}:8089/ws`;

        const uaOptions: UserAgentOptions = {
            uri,
            transportOptions: { server: wsServer },
            authorizationUsername: username,
            authorizationPassword: password,
            instanceId: this.instanceId,
            sessionDescriptionHandlerFactoryOptions: {
                rtcConfiguration: {
                    iceServers: stunServers.map(url => ({ urls: url })),
                },
                constraints: { audio: true, video: false },
            },
            sendInitialProvisionalResponse: false,
        };

        const ua = new UserAgent(uaOptions);

        ua.delegate = {
            onInvite: (invitation: Invitation) =>
                this.handleIncomingCall(invitation),
            onDisconnect: () => {
                if (!this.shuttingDown)
                    this.destroyBinding(serverHost).then(() =>
                        this.reconnectBinding(serverHost, stunServers, username, password),
                    );
            },
        };

        const transport: any = (ua as any).transport;
        if (transport?.stateChange?.addListener) {
            transport.stateChange.addListener((state: string) => {
                console.warn('[SIP] transport state:', state);
                if (
                    !this.shuttingDown &&
                    !this.hasActiveCall() &&
                    (state === 'Disconnected' || state === 'Closed')
                ) {
                    this.destroyBinding(serverHost).then(() =>
                        this.reconnectBinding(serverHost, stunServers, username, password),
                    );
                }
            });
        } else {
            const ws: WebSocket | undefined = (transport?._transport as any)
                ?._ws;
            if (ws) {
                ws.addEventListener('close', () => {
                    console.warn('[SIP] WS close');
                    if (!this.shuttingDown && !this.hasActiveCall()) {
                        this.destroyBinding(serverHost).then(() =>
                            this.reconnectBinding(serverHost, stunServers, username, password),
                        );
                    }
                });
                ws.addEventListener('error', () => {
                    console.warn('[SIP] WS error');
                });
            }
        }

        const registererOptions: RegistererOptions = { expires: 60 };
        const registerer = new Registerer(ua, registererOptions);

        await ua.start();
        await this.runRegOp(registerer, () => registerer.register());
        this.lastRegisterAt = Date.now();

        this.bindings.set(serverHost, { ua, registerer });

        registerer.stateChange.addListener(s => {
            if (s === RegistererState.Registered) {
                this.registrationStatus.set('Registered');
                this.lastRegisterAt = Date.now();
            } else if (s === RegistererState.Unregistered) {
                this.registrationStatus.set('Unregistered');
            }
        });
    }

    private handleIncomingCall(invitation: Invitation): void {
        if (this.callState() === 'in-call') {
            invitation.reject({
                statusCode: 486,
                reasonPhrase: 'Busy Here',
            });
            console.log('Rejected incoming call - already in call');
            return;
        }
        invitation.progress();
        this.stopRingingSound();
        this.incomingInvitation.set(invitation);
        this.callState.set('ringing');

        invitation.stateChange.addListener(state => {
            if (state === SessionState.Terminated) {
                this.stopRingingSound();
                if (this.incomingInvitation() === invitation) {
                    this.incomingInvitation.set(null);
                    this.callState.set('ended');
                }
            }
        });

        if (this.autoAnswer()) {
            this.autoAnswer.set(false);
            setTimeout(() => this.answerCall(), 0);
        } else {
            this.playRingingSound();
        }
    }

    private stopLocalStream(): void {
        const s = this.localStream();
        if (s) {
            s.getTracks().forEach(t => t.stop());
            this.localStream.set(null);
        }
    }

    private playRingingSound(): void {
        try {
            this.ringingAudio.play();
        } catch (e) {
            console.error('Ringing play error:', e);
        }
    }

    private stopRingingSound(): void {
        this.ringingAudio.pause();
        this.ringingAudio.currentTime = 0;
    }

    private async checkConnectivity(): Promise<void> {
        if (this.shuttingDown || this.hasActiveCall() || this.lastServers.length === 0) return;

        for (const server of this.lastServers) {
            const binding = this.bindings.get(server.uri);
            if (!binding) {
                console.warn('[SIP] checkConnectivity: missing binding, reconnecting', server.uri);
                await this.reconnectBinding(server.uri, this.lastStunServers, this.lastUsername, this.lastPassword);
            } else if (binding.registerer.state !== RegistererState.Registered) {
                console.warn('[SIP] checkConnectivity: not registered, re-registering', server.uri);
                try {
                    await this.runRegOp(binding.registerer, () => binding.registerer.register());
                } catch {
                    await this.destroyBinding(server.uri);
                    await this.reconnectBinding(server.uri, this.lastStunServers, this.lastUsername, this.lastPassword);
                }
            }
        }
    }

    private reconcileServers(): void {
        if (this.shuttingDown || this.hasActiveCall() || this.lastServers.length === 0) return;

        // Derive domain from existing server URIs (e.g. "call-view.com" from "server1.call-view.com")
        const domain = this.lastServers[0]?.uri.split('.').slice(1).join('.');
        if (!domain) return;

        forkJoin({
            servers: this.voiceApiService.getAsteriskServers(),
            stunServers: this.voiceApiService.getAsteriskStunServers(),
            login: this.voiceApiService.getAsteriskLogin(),
        })
            .pipe(take(1))
            .subscribe(async ({ servers, stunServers, login }) => {
                if (!servers.isSuccess || !login.isSuccess) return;

                const freshServers = (servers.data ?? []).map(s => ({ uri: `${s.server}.${domain}` }));
                const freshStunServers = (stunServers.data ?? []).map(s => s.dataValue);
                const login0 = login.data?.[0];
                if (!login0) return;

                const freshUsername = `webrtc_${login0.username}`;
                const freshPassword = login0.secret;

                const currentUris = new Set(this.lastServers.map(s => s.uri));
                const freshUris = new Set(freshServers.map(s => s.uri));

                // Unregister servers no longer in the list
                for (const uri of currentUris) {
                    if (!freshUris.has(uri)) {
                        console.warn('[SIP] reconcileServers: removing server', uri);
                        await this.destroyBinding(uri);
                    }
                }

                // Update stored credentials and server list
                this.lastServers = freshServers;
                this.lastStunServers = freshStunServers;
                this.lastUsername = freshUsername;
                this.lastPassword = freshPassword;

                // Register any new servers
                for (const uri of freshUris) {
                    if (!currentUris.has(uri)) {
                        console.warn('[SIP] reconcileServers: adding new server', uri);
                        await this.reconnectBinding(uri, freshStunServers, freshUsername, freshPassword);
                    }
                }
            });
    }

    private async destroyBinding(serverHost: ServerHost): Promise<void> {
        const binding = this.bindings.get(serverHost);
        if (!binding) return;
        const { ua, registerer } = binding;
        try {
            await this.runRegOp(registerer, () =>
                registerer.unregister({ all: true }),
            );
        } catch (e) {
            console.error('registerer.unregister:', e);
        }
        try {
            await ua.stop();
        } catch (e) {
            console.error('ua.stop:', e);
        }
        this.bindings.delete(serverHost);
    }

    private async reconnectBinding(
        serverHost: ServerHost,
        stunServers: string[],
        username: string,
        password: string,
    ): Promise<void> {
        if (this.shuttingDown) return;
        console.warn('[SIP] reconnecting binding:', serverHost);
        try {
            await this.registerWithServer(serverHost, stunServers, username, password);
        } catch (e) {
            console.error('[SIP] reconnect failed:', serverHost, e);
        }
    }

    private async destroyAll(): Promise<void> {
        this.shuttingDown = true;
        const tasks: Promise<unknown>[] = [];
        for (const [key, { ua, registerer }] of this.bindings.entries()) {
            tasks.push(
                (async () => {
                    try {
                        await this.runRegOp(registerer, () =>
                            registerer.unregister({ all: true }),
                        );
                    } catch (e) {
                        console.error('registerer.unregister:', e);
                    }
                    try {
                        await ua.stop();
                    } catch (e) {
                        console.error('ua.stop:', e);
                    }
                    this.bindings.delete(key);
                })(),
            );
        }
        await Promise.all(tasks);
        this.incomingInvitation.set(null);
        this.currentSession = null;
        this.peerConnection = undefined;
        this.stopLocalStream();
        this.stopRingingSound();

        this.voiceApiService.sipState('disconnected', {
            agentId: this.user().id,
            tenantId: this.user().tenant?.id ?? '',
        });
        this.shuttingDown = false;
    }

    async refreshInitializeSipClients() {
        if (this.lastServers.length === 0 || !this.lastUsername) {
            console.warn(
                'No stored SIP credentials/servers � call initializeSipClients first.',
            );
            return;
        }
        await this.initializeSipClients(
            this.lastServers,
            this.lastStunServers,
            this.lastUsername,
            this.lastPassword,
        );
    }

    private hasActiveCall() {
        return (
            this.currentSession &&
            this.currentSession.state === SessionState.Established
        );
    }

    private async refreshInitializeSipClientsSafe(force = false) {
        if (this.shuttingDown || this.hasActiveCall()) return;

        if (!force && Date.now() - this.lastRegisterAt < this.safetyRefreshMs)
            return;

        if (this.bindings.size > 0) {
            for (const { registerer } of this.bindings.values()) {
                try {
                    await this.runRegOp(registerer, () =>
                        registerer.register(),
                    );
                    this.lastRegisterAt = Date.now();
                } catch {
                    await this.refreshInitializeSipClients();
                    return;
                }
            }
        } else {
            await this.refreshInitializeSipClients();
        }
    }
}
